/**
 * Website shared styles + helpers.
 * All website pages use a light theme (paper background, ink text).
 */
import { DB } from '../../data/database';

// ── Company info from app_config ──
export function getCompanyInfo() {
  try {
    const cfg = DB.get('appConfig');
    if (cfg && typeof cfg === 'object' && !Array.isArray(cfg)) return cfg.company_info || {};
    const raw = localStorage.getItem('bigstar_appConfig');
    if (raw) { const p = JSON.parse(raw); if (p?.company_info) return p.company_info; }
  } catch {}
  return {};
}

export function getLogoUrl() { return getCompanyInfo().logo_url || ''; }
export function getContact() {
  const ci = getCompanyInfo();
  const whatsappRaw = ci.whatsapp_number || ci.mobile1 || '+971505660816';
  return {
    phone: ci.telephone || '050-858-3625',
    mobile: ci.mobile1 || ci.whatsapp_number || '+971505660816',
    email: ci.email || 'info@bigstaruae.com',
    whatsapp: whatsappRaw.replace(/[^\d+]/g, ''),
    address: ci.address || 'Industrial Area No. 3, Sharjah, UAE',
    name: ci.name || 'BIG STAR USED CARS & SPARE PARTS TR. L.L.C.',
  };
}

// ── All website units (show_on_website === true) ──
export function getWebsiteUnits() {
  let soldDays = 14;
  try {
    const cfg = DB.get('appConfig');
    if (cfg && typeof cfg === 'object' && !Array.isArray(cfg) && cfg.sold_visibility_days != null) {
      soldDays = Number(cfg.sold_visibility_days);
    }
  } catch {}
  const cutoff = Date.now() - soldDays * 86400000;

  const visible = u => {
    if (!u.show_on_website || u.website_status === 'hidden') return false;
    if (u.website_status === 'sold' || isSold(u)) {
      if (!u.sold_at) return true;
      return new Date(u.sold_at).getTime() > cutoff;
    }
    return true;
  };
  const v = (DB.get('vehicles') || []).filter(visible).map(u => ({ ...u, _type: 'vehicle' }));
  const e = (DB.get('engines') || []).filter(visible).map(u => ({ ...u, _type: 'engine' }));
  const p = (DB.get('spareParts') || []).filter(visible).map(u => ({ ...u, _type: 'part' }));
  return [...v, ...e, ...p];
}

export function isSold(u) {
  return ['sold', 'sold_awaiting_payment', 'loading', 'in_transit', 'shipped', 'delivered', 'dismantled'].includes(u.status) || u.website_status === 'sold';
}

export function getUnitName(u) {
  return u.make ? `${u.year ? u.year + ' ' : ''}${u.make} ${u.model || ''}`.trim() : u.engineName || u.partName || 'Unit';
}

// ── Page content from CMS (app_config) ──
export function getPageContent(pageKey) {
  try {
    const cfg = DB.get('appConfig');
    if (cfg && typeof cfg === 'object' && !Array.isArray(cfg) && cfg[pageKey]) {
      return cfg[pageKey];
    }
  } catch {}
  return null; // components use their own defaults when null
}

// Refresh appConfig from Supabase in BACKGROUND (non-blocking)
// Page renders instantly from localStorage cache, Supabase updates silently
let _refreshing = false;
export function refreshContent(onRefreshed) {
  if (_refreshing) return;
  _refreshing = true;
  // Fire-and-forget — don't await, don't block rendering
  DB.refresh('appConfig')
    .then(() => { if (onRefreshed) onRefreshed(); })
    .catch(() => {})
    .finally(() => { setTimeout(() => { _refreshing = false; }, 60000); });
}

// ── Shared style constants ──
export const WS = {
  page: { minHeight: '100vh', background: 'var(--bs-paper)', color: 'var(--bs-ink)', fontFamily: '"Inter Tight", "Inter", system-ui, sans-serif' },
  maxW: { maxWidth: 1200, margin: '0 auto', padding: '0 20px' },
  heading: { fontFamily: '"Inter Tight", system-ui, sans-serif', fontWeight: 800, letterSpacing: '-0.04em', color: 'var(--bs-ink)' },
  subtext: { color: 'var(--bs-ink-3)', fontSize: 15, lineHeight: 1.6 },
  accent: { color: 'var(--bs-accent)' },
  card: { background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 0 rgba(20,22,28,0.04), 0 6px 24px rgba(20,22,28,0.06)', transition: 'box-shadow 0.2s, transform 0.2s', cursor: 'pointer' },
  btn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 24px', borderRadius: 8, fontWeight: 600, fontSize: 14, border: 'none', cursor: 'pointer', transition: 'all 0.15s' },
  btnPrimary: { background: 'var(--bs-accent)', color: '#fff' },
  btnSecondary: { background: 'transparent', color: 'var(--bs-ink)', border: '1px solid var(--bs-line-2)' },
  section: { padding: '80px 0' },
  sectionAlt: { padding: '80px 0', background: 'var(--bs-paper-2)' },
  eyebrow: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--bs-accent)', marginBottom: 12 },
  divider: { height: 1, background: 'var(--bs-line)', margin: '0' },
};
